import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import CheckboxField from 'components/application/CheckboxField';
import CircleQuestion from 'components/application/CircleQuestion';
import {
  DropdownIndicator,
  MultiValueContainer,
  Option,
} from 'components/application/CollectionSelect';
import Tooltip from 'components/application/Tooltip';

import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';

const autoEnrolCourseRequirementScopeOptions = [
  { tKey: 'required', value: 'required' },
  { tKey: 'requiredOrOptional', value: 'all' },
];

const autoEnrolEnrolleeScopeOptions = [
  { tKey: 'employees', value: 'employees' },
  { tKey: 'employeesAndSubcontractors', value: 'all' },
];

export default function CourseSidePanelAutoEnrolmentForm({
  autoEnrolCourseRequirementScope,
  autoEnrolEnrolleeScope,
  hasAutoEnrolBeenActivelySelected,
  isExpiringCourse,
  onInputChange,
  onOptionChange,
}) {
  const { isAutoEnrolmentIncludingMissingTraining } =
    useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelAutoEnrolmentForm',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const isChecked =
    hasAutoEnrolBeenActivelySelected &&
    (isAutoEnrolmentIncludingMissingTraining || isExpiringCourse);

  const isDisabled = (function determineIsDisabled() {
    return !isAutoEnrolmentIncludingMissingTraining && !isExpiringCourse;
  })();

  const selectedAutoEnrolCourseRequirementScopeOption =
    autoEnrolCourseRequirementScopeOptions.find(
      (option) => option.value === autoEnrolCourseRequirementScope,
    );

  const selectedAutoEnrolEnrolleeScopeOption =
    autoEnrolEnrolleeScopeOptions.find(
      (option) => option.value === autoEnrolEnrolleeScope,
    );

  return (
    <>
      <div className='tw-mb-5 tw-mt-5 tw-flex'>
        <CheckboxField
          checked={isChecked}
          className='tw-w-auto'
          customStyling='peer-disabled/input:tw-bg-grey-050 peer-disabled/input:tw-border-grey-300 peer-disabled/input:tw-opacity-100'
          disabled={isDisabled}
          height='short'
          label={i18nT('hasAutoEnrolBeenActivelySelected.label')}
          name='hasAutoEnrolBeenActivelySelected'
          onChange={onInputChange}
          tooltip={
            isDisabled ?
              i18nT('hasAutoEnrolBeenActivelySelected.disabledTooltip')
            : null
          }
          tooltipType={isDisabled ? 'hover' : null}
        />
        <div className='tw-ml-2'>
          <Tooltip
            className='tooltip-dark--max-w-xxs'
            placement='top'
            tooltip={i18nT('hasAutoEnrolBeenActivelySelected.tooltip')}
            trigger='hover'
          >
            <CircleQuestion />
          </Tooltip>
        </div>
      </div>
      {isChecked && (
        <>
          <div className='tw-mb-3 tw-ml-7 tw-flex tw-items-center'>
            <label className='tw-mb-0 tw-mr-2 tw-font-normal'>
              {i18nT('autoEnrolEnrolleeScope.label')}
            </label>
            <div className='collection-select collection-select--inline-form'>
              <Select
                backspaceRemovesValue={false}
                className='collection-select__select-container'
                classNamePrefix='collection-select'
                closeMenuOnSelect={true}
                components={{ Option, MultiValueContainer, DropdownIndicator }}
                customMargin='tw-mb-8'
                hideSelectedOptions={false}
                isClearable={false}
                isMulti={false}
                isSearchable={false}
                maxMenuHeight='unset'
                name='autoEnrolEnrolleeScope'
                onChange={onOptionChange}
                options={autoEnrolEnrolleeScopeOptions.map((option) => ({
                  label: i18nTShared(option.tKey),
                  value: option.value,
                }))}
                styles={{
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    width: 'max-content',
                    minWidth: '100%',
                  }),
                  /* eslint-disable no-unused-vars */
                  singleValue: ({
                    maxWidth,
                    position,
                    top,
                    transform,
                    ...otherStyles
                  }) => ({ ...otherStyles }),
                  /* eslint-enable no-unused-vars */
                }}
                value={
                  selectedAutoEnrolEnrolleeScopeOption && {
                    label: i18nTShared(
                      selectedAutoEnrolEnrolleeScopeOption.tKey,
                    ),
                    value: autoEnrolEnrolleeScope,
                  }
                }
              />
            </div>
          </div>
          <div className='tw-mb-3 tw-ml-7 tw-flex tw-items-center'>
            <label className='tw-mb-0 tw-mr-2 tw-font-normal'>
              {i18nT('autoEnrolCourseRequirementScope.label')}
            </label>
            <div className='collection-select collection-select--inline-form'>
              <Select
                backspaceRemovesValue={false}
                className='collection-select__select-container'
                classNamePrefix='collection-select'
                closeMenuOnSelect={true}
                components={{ Option, MultiValueContainer, DropdownIndicator }}
                customMargin='tw-mb-8'
                hideSelectedOptions={false}
                isClearable={false}
                isMulti={false}
                isSearchable={false}
                maxMenuHeight='unset'
                name='autoEnrolCourseRequirementScope'
                onChange={onOptionChange}
                options={autoEnrolCourseRequirementScopeOptions.map(
                  (option) => ({
                    label: i18nTShared(option.tKey),
                    value: option.value,
                  }),
                )}
                styles={{
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    width: 'max-content',
                    minWidth: '100%',
                  }),
                  /* eslint-disable no-unused-vars */
                  singleValue: ({
                    maxWidth,
                    position,
                    top,
                    transform,
                    ...otherStyles
                  }) => ({ ...otherStyles }),
                  /* eslint-enable no-unused-vars */
                }}
                value={
                  selectedAutoEnrolCourseRequirementScopeOption && {
                    label: i18nTShared(
                      selectedAutoEnrolCourseRequirementScopeOption.tKey,
                    ),
                    value: autoEnrolCourseRequirementScope,
                  }
                }
              />
            </div>
          </div>
        </>
      )}
    </>
  );
}

CourseSidePanelAutoEnrolmentForm.propTypes = {
  hasAutoEnrolBeenActivelySelected: PropTypes.bool.isRequired,
  autoEnrolEnrolleeScope: PropTypes.string.isRequired,
  autoEnrolCourseRequirementScope: PropTypes.string.isRequired,
  isExpiringCourse: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onOptionChange: PropTypes.func.isRequired,
};
